export interface AttendanceStatusConfig {
  workStartTime: string
  workEndTime: string
  graceMinutes: number
  halfDayThresholdMinutes: number
  dailyWorkingHours: number
}

export type ComputedAttendanceStatus = 'PRESENT' | 'LATE' | 'HALF_DAY' | 'ABSENT'

export function timeStringToMinutes(timeStr?: string): number | null {
  if (!timeStr || !timeStr.trim()) return null
  const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!match) return null
  const hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)
  if (isNaN(hours) || hours < 0 || hours > 23 || isNaN(minutes) || minutes < 0 || minutes > 59) {
    return null
  }
  return hours * 60 + minutes
}

/**
 * Pure function to compute attendance status based on check-in/check-out times and shift configuration.
 * Returns null if checkOut is earlier than checkIn (invalid time range) or if config is not yet loaded.
 * Note: The backend recomputes the final attendance status on save (evaluating approved leaves & shift rules).
 */
export function computeAttendanceStatus(
  checkIn?: string,
  checkOut?: string,
  config?: AttendanceStatusConfig | null
): ComputedAttendanceStatus | null {
  if (!config) {
    return null
  }

  const inMin = timeStringToMinutes(checkIn)
  const outMin = timeStringToMinutes(checkOut)

  // If check-out is earlier than check-in, mark row invalid
  if (inMin !== null && outMin !== null && outMin < inMin) {
    return null
  }

  // No check-in -> ABSENT
  if (inMin === null) {
    return 'ABSENT'
  }

  const startMin = timeStringToMinutes(config.workStartTime) ?? 8 * 60
  const lateCutoffMin = startMin + config.graceMinutes

  // Base status: Check-in later than workStartTime + graceMinutes -> LATE, otherwise PRESENT
  const baseStatus: 'PRESENT' | 'LATE' = inMin > lateCutoffMin ? 'LATE' : 'PRESENT'

  // If check-out is set
  if (outMin !== null) {
    const workedMinutes = outMin - inMin

    // Precedence: ABSENT (< halfDayThresholdMinutes), then HALF_DAY (< dailyWorkingHours * 60), then baseStatus
    if (workedMinutes < config.halfDayThresholdMinutes) {
      return 'ABSENT'
    }
    if (workedMinutes < config.dailyWorkingHours * 60) {
      return 'HALF_DAY'
    }
    return baseStatus
  }

  return baseStatus
}
